import { Warehouse } from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import { LoadingButton } from "@mui/lab";
import { Grid, Stack } from "@mui/material";
import { FormDatePicker, FormTextField } from "components/atoms";
import { DetailTableTabs, ReportHeader } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import { TimeHelper } from "helpers";
import { IDownloadExcelRequest, IGetAllLocationsReportRequest } from "model";
import moment from "moment";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { DownloadActions } from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { EXPORT_FILE_NAME } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";
import { LocationList, ProductLots, ZoneDetail, Zones } from "./components";
import NewLocation from "./components/NewLocation";

export enum ITypeLocationReport {
  ALL = 0,
  WAREHOUSE_OWNER = 1,
  FORKLIFT = 2,
}

const WarehouseDetailReport = () => {
  const [request, setRequest] = useState<IGetAllLocationsReportRequest>({
    warehouse: "",
    productLot: "",
    product: "",
    line: "",
    user: "",
    zone: "",
    type: ITypeLocationReport.ALL,
    fromDate: TimeHelper.startOfWeek("MM-DD-YYYY"),
    toDate: TimeHelper.endOfWeek("MM-DD-YYYY"),
  });

  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        height: "100%",
      }}
    >
      <DetailHeader request={request} onSubmit={setRequest} />
      <TabTablesSection request={request} />
    </div>
  );
};

const DetailHeader = (props: {
  request: IGetAllLocationsReportRequest;
  onSubmit(request: IGetAllLocationsReportRequest): void;
}) => {
  const history = useHistory();

  const dispatch = useAppDispatch();
  const { request, onSubmit } = props;

  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );
  const formik = useFormik<any>({
    initialValues: {
      ...request,
      fromDate: moment(request.fromDate, "MM-DD-YYYY"),
      toDate: moment(request.toDate, "MM-DD-YYYY"),
    },
    onSubmit: (values: any, { validateForm }: FormikHelpers<any>) => {

      onSubmit({
        ...values,
        type: 0,
        pageNumber: 1,
        fromDate: TimeHelper.formatMomentToDate(values.fromDate, "MM-DD-YYYY"),
        toDate: TimeHelper.formatMomentToDate(values.toDate, "MM-DD-YYYY"),
      });
    },
  });

  const onDownloadLocationReport = async () => {
    const values = formik.values;
    const params: IDownloadExcelRequest = {
      fromDate: TimeHelper.formatMomentToDate(values.fromDate, "YYYY-MM-DD"),
      toDate: TimeHelper.formatMomentToDate(values.toDate, "YYYY-MM-DD"),
      fileName: EXPORT_FILE_NAME.warehouseReport,
    };
    dispatch(DownloadActions.downloadWarehouseReport.request(params));
  };

  return (
    <ReportHeader
      title={I18nTranslator(translations.routes.warehouseDetail)}
      formik={formik}
      loading={false}
      renderButtons={
        <Stack direction={"row"} spacing={2}>
          <LoadingButton
            loading={downloading}
            onClick={() => history.push("/report/warehouse-layout")}
            startIcon={<Warehouse />}
            variant="contained"
          >
            {I18nTranslator(translations.button.warehouseLayout)}
          </LoadingButton>
          <LoadingButton
            loading={downloading}
            onClick={onDownloadLocationReport}
            startIcon={<DownloadIcon />}
            variant="contained"
          >
            {I18nTranslator(translations.button.exportDocs)}
          </LoadingButton>
        </Stack>
      }
    >
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={3} item>
          <FormDatePicker
            id="fromDate"
            label={I18nTranslator(translations.fromDate)}
          />
        </Grid>
        <Grid xs={3} item>
          <FormDatePicker
            id="toDate"
            label={I18nTranslator(translations.toDate)}
          />
        </Grid>
        <Grid xs={3} item>
          <FormTextField
            id="product"
            label={I18nTranslator(translations.report.productName)}
            placeholder={I18nTranslator(translations.placeholder.productName)}
          />
        </Grid>
        <Grid xs={3} item>
          <FormTextField
            id="productLot"
            label={I18nTranslator(translations.report.productLotOrPallet)}
            placeholder={I18nTranslator(
              translations.placeholder.productLotOrPallet
            )}
          />
        </Grid>
      </Grid>
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={3} item>
          <FormTextField
            id="user"
            label={I18nTranslator(translations.report.executor)}
            placeholder={I18nTranslator(translations.placeholder.executor)}
          />
        </Grid>
        <Grid xs={3} item>
          <FormTextField
            label={I18nTranslator(translations.report.warehouse)}
            placeholder={I18nTranslator(translations.placeholder.warehouseName)}
            id="warehouse"
          />
        </Grid>
        <Grid xs={3} item>
          <FormTextField
            label={I18nTranslator(translations.report.zone)}
            placeholder={I18nTranslator(translations.placeholder.zoneName)}
            id="zone"
          />
        </Grid>
        <Grid xs={3} item>
          <FormTextField
            label={I18nTranslator(translations.report.line)}
            placeholder={I18nTranslator(translations.placeholder.line)}
            id="line"
          />
        </Grid>
      </Grid>
    </ReportHeader>
  );
};

const TabTablesSection = (props: {
  request: IGetAllLocationsReportRequest;
}) => {
  const { request } = props;
  const tabsName = [
    I18nTranslator(translations.report.zoneList),
    I18nTranslator(translations.report.zoneDetail),
    I18nTranslator(translations.report.locationList),
    I18nTranslator(translations.report.emptyLocationList),
    I18nTranslator(translations.report.productLotList),
  ];

  const tabsPanel = [
    <Zones />,
    <ZoneDetail />,
    <LocationList request={request} />,
    <NewLocation request={request} />,
    <ProductLots request={request} />,
  ];

  return (
    <DetailTableTabs
      hideHeaderButton
      headerButtons={[]}
      tabsName={tabsName}
      tabsPanel={tabsPanel}
    />
  );
};

export default WarehouseDetailReport;
